import React, { useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCreditCard, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { BROKER_PRICING_TIERS } from '../../constants/brokeragePricing';

interface BrokerageSubscriptionModalProps {
  show: boolean;
  onHide: () => void;
  tier: 'standard' | 'stability' | 'small' | 'medium' | 'large';
  price: number;
  onConfirm: (tier: 'standard' | 'stability' | 'small' | 'medium' | 'large') => Promise<void>;
}

const BrokerageSubscriptionModal: React.FC<BrokerageSubscriptionModalProps> = ({
  show,
  onHide,
  tier,
  price,
  onConfirm
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tierNames = {
    standard: 'Standard Office Plan',
    stability: 'Stability Plan',
    small: 'Small Brokerage',
    medium: 'Medium Brokerage',
    large: 'Large Brokerage'
  };

  const tierRanges = {
    standard: 'Unlimited agents',
    stability: '< 20 agents',
    small: '< 50 agents',
    medium: '50-200 agents',
    large: '200+ agents'
  };

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await onConfirm(tier);
    } catch (err: any) {
      setError(err.message || 'Failed to process subscription. Please try again.');
      setIsLoading(false);
    }
  };

  // Calculate next billing date (1 month from now)
  const nextBillingDate = new Date();
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  const formattedDate = nextBillingDate.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="d-flex align-items-center">
          <div
          
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: 'white'
            }}
          >
            <FontAwesomeIcon icon={faUsers} size="lg" />
          </div>
          <div>
            <h5 className="mb-0 fw-bold">Subscribe Now</h5>
            <p className="text-muted mb-0 small">Setup your new subscription</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-4">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Billing Details Section */}
        <div className="mb-4">
          <div
            className="d-flex align-items-center mb-3 p-2 rounded"
            style={{ background: 'rgba(99, 102, 241, 0.05)' }}
          >
            <FontAwesomeIcon icon={faCreditCard} className="me-2" style={{ color: '#6366f1' }} />
            <span className="fw-semibold" style={{ color: '#1f2937' }}>
              Billing Details
            </span>
          </div>

          <div
            className="d-flex justify-content-between align-items-center p-3 rounded"
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb'
            }}
          >
            <div>
              <div className="fw-semibold" style={{ color: '#1f2937' }}>
                {tierNames[tier]} (monthly):
              </div>
              <div className="text-muted small">{tierRanges[tier]}</div>
            </div>
            <div className="fw-bold" style={{ fontSize: '20px', color: '#1f2937' }}>
              ${price.toLocaleString()}.00
            </div>
          </div>
        </div>

        {/* Total Due */}
        <div className="mb-4 pb-4" style={{ borderBottom: '2px solid #e5e7eb' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-bold" style={{ fontSize: '18px', color: '#1f2937' }}>
              Total due today:
            </span>
            <span className="fw-bold" style={{ fontSize: '24px', color: '#10b981' }}>
              ${price.toLocaleString()}.00
            </span>
          </div>
        </div>

        {/* Subscription Benefits */}
        <div
          className="p-3 rounded mb-3"
          style={{
            background: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}
        >
          <div className="d-flex align-items-start">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3 mt-1"
              style={{
                width: '24px',
                height: '24px',
                minWidth: '24px',
                background: '#3b82f6',
                color: 'white',
                fontSize: '12px'
              }}
            >
              ✓
            </div>
            <div>
              <div className="fw-semibold mb-1" style={{ color: '#1f2937' }}>
                Subscription Benefits
              </div>
              <div className="text-muted small">
                Your subscription starts immediately after payment. Cancel anytime from your settings.
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center mt-3 ps-4">
            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: '#3b82f6' }} />
            <span className="small text-muted">Next billing: {formattedDate}</span>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-muted small">
          <p className="mb-2">
            <strong>What's included:</strong>
          </p>
          <ul className="mb-0 ps-3">
            <li>GPT-4 Vision AI Technology</li>
            <li>All AI features and compliance tools</li>
            <li>Team management dashboard</li>
            <li>Document management system</li>
            <li>Priority support</li>
            <li>${BROKER_PRICING_TIERS.agentFee}/month per additional agent</li>
          </ul>
        </div>
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <Button
          variant="light"
          onClick={onHide}
          disabled={isLoading}
          style={{
            padding: '10px 24px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubscribe}
          disabled={isLoading}
          style={{
            padding: '10px 32px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
            minWidth: '200px'
          }}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              Subscribe to {tierNames[tier]}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BrokerageSubscriptionModal;
